'use client';
import React from "react";
import { Switch as HeadlessSwitch } from "@headlessui/react";

import Spinner from "../../Spinner/Spinner";

import styles from "./Switch.module.css";
import { cn } from "../../../../utils/cn";

type HeadlessSwitchProps = React.ComponentProps<typeof HeadlessSwitch>;

interface CheckboxEvent {
    target: {
        value: boolean;
        checked: boolean;
        name: string;
    };
    type: 'checkbox';
}

interface Props extends Pick<HeadlessSwitchProps, 'as' | 'disabled' | 'className'> {
    isOn?: HeadlessSwitchProps['checked'];
    onChange?: (isOn: boolean, event: CheckboxEvent) => void;
    label: string;
    name?: string;
    loading?: boolean;
}

const Switch = ({ as, isOn, onChange, disabled, label, className, name, loading }: Props) => {
    const classNames = cn(
        {
            "!bg-content-foundation": isOn,
            "!bg-content-soft": !isOn,
            "!bg-layers-muted !pointer-events-none": disabled,
        },
        styles.track,
        className
    );

    const dotCn = cn(
        {
            "!left-[1.75rem] !bg-content-accent": isOn,
            "!left-[0.25rem] !bg-content-foundation": !isOn,
            "!bg-decorators-default": disabled,
        },
        styles.dot
    );

    const handleChange = !disabled && !loading && onChange ? () => {
        const value = !isOn || false
        onChange(value, {
            target: {
                value,
                checked: value,
                name: name || ''
            },
            type: 'checkbox',
        });
    } : undefined;

    return (
        <HeadlessSwitch as={as} checked={isOn} onChange={handleChange} name={name} className={classNames}>
            <span className="sr-only">{label}</span>
            <span className={dotCn}>
                {loading ? (
                    <Spinner className={`${styles.spinner} ${isOn ? 'fill-content-foundation' : 'fill-content-accent'}`} />
                ) : null}
            </span>
        </HeadlessSwitch>
    );
};

export default Switch;
